<template>
    <!--
        `template-com-midia` prende a largura do conteúdo quando há cabeçalho de
        mídia. Sem isso, um corpo longo esticava a bolha até a largura de uma
        mensagem de TEXTO, e a imagem — que é `width: 100%` — ia junto, deformada.
        O comportamento certo é o da imagem com legenda: a mídia manda na largura
        e o texto se acomoda embaixo dela.
    -->
    <div v-if="message.type == 'template_meta' && message.metaTemplateData"
        :class="{ 'template-com-midia': cTemMidiaNoCabecalho }">
        <template v-if="cTemMidiaNoCabecalho && cHeaderFileData">
            <ChatMessageMedia :message="{ type: 'template_meta', fileData: cHeaderFileData }" />
        </template>
        <template v-else-if="cTypes.header?.includes('TEXT') && cContent.header">
            <ChatMessageText :message="{ id: '', text: cTexto.header }" :isBold="true" />
        </template>
        <ChatMessageText v-if="cContent.body" :message="{ id: '', text: cTexto.body }" />
        <ChatMessageText v-if="cContent.footer" :message="{ id: '', text: cContent.footer.text }" :isSmall="true" />

        <!--
            Botões do template.
            Não eram desenhados: a bolha mostrava cabeçalho, corpo e rodapé e parava
            aí, então um template com duas respostas rápidas chegava ao operador sem
            sinal nenhum de que o contato tinha o que clicar — e a resposta dele vinha
            sem contexto. São READ-ONLY de propósito: quem clica é o destinatário, no
            aparelho dele. O `disabled` existe para dizer isso, não para esconder.
            Os que NÃO devolvem resposta (link, ligar, copiar código, OTP) entram
            também, com o ícone da variante: eles fazem parte do que o cliente vê.
        -->
        <div v-if="cBotoes.length" class="template-botoes">
            <button v-for="(botao, i) of cBotoes" :key="`tplbtn-${i}`" type="button" class="template-botao" disabled
                :title="botao.respondivel
                    ? 'Resposta rápida — o contato responde tocando aqui no aparelho dele.'
                    : 'Este botão age no aparelho do contato e não devolve resposta.'">
                <i :class="botao.icon"></i>
                <span>{{ botao.texto }}</span>
            </button>
        </div>

        <!-- Template RECUSADO pela Meta. A bolha existe justamente para o operador ver
             que a tentativa aconteceu e por que ela não saiu — antes, uma recusa não
             deixava rastro nenhum no chat. -->
        <FalhaEnvioMeta v-if="cFalhou" :codigo="message.metaTemplateData?.erroCodigo"
            :motivo="cMotivoFalha" :detalhe="message.metaTemplateData?.erroDetalhe" />
    </div>
</template>
<script setup lang="ts">
import FalhaEnvioMeta from './FalhaEnvioMeta.vue';
import { computed, PropType } from 'vue';
import ChatMessageMedia from './ChatMessageMedia.vue';
import ChatMessageText from './ChatMessageText.vue';
import { FileData, Message } from '@/mocks/app/mockChatData';
import { getBodyParser, getHeaderParser } from '@/services/templates-wpp-meta/meta-wpp-template.service';
import { aplicarParametros } from '@/services/templates-wpp-meta/meta-template-params';
import { MetaTemplateFooter } from '@/services/templates-wpp-meta/template-footer';
import { MetaUploadsService } from '@/services/meta-uploads/meta-uploads.service';
import { botoesDoTemplate } from '@/services/templates-wpp-meta/meta-template-preview';

const props = defineProps({
    message: { type: Object as PropType<Message>, required: true }
})

const cTemplate = computed(() => props.message.metaTemplateData?.template)
const cTypes = computed(() => ({
    header: cTemplate.value?.headerType,
    body  : cTemplate.value?.bodyType,
    footer: cTemplate.value?.footerType,
}));
const cContent = computed(() => ({
    header: cTypes.value.header && cTemplate.value?.headerJson ? getHeaderParser(cTypes.value.header).parse(cTemplate.value.headerJson): null,
    body  : cTypes.value.body   && cTemplate.value?.bodyJson ? getBodyParser(cTypes.value.body)      .parse(cTemplate.value.bodyJson)    : null,
    footer: cTypes.value.footer && cTemplate.value?.footerJson ? new MetaTemplateFooter()            .parse(cTemplate.value.footerJson): null,
}));

/**
 * O texto que o destinatário REALMENTE recebeu.
 *
 * `bodyJson.text` é o template cru, com os `{{...}}` — era ele que a bolha
 * imprimia, enquanto os valores viajavam ao lado, em `parametersToReplace`, sem
 * nunca serem aplicados. O rodapé fica de fora porque a Meta não aceita variável
 * nele.
 */
const cParams = computed(() => props.message.metaTemplateData?.parametersToReplace ?? null);
const cTexto = computed(() => ({
    header: aplicarParametros(cContent.value.header?.text, cParams.value, 'header'),
    body  : aplicarParametros(cContent.value.body?.text, cParams.value, 'body'),
}));

/**
 * `sendSuccess` é a única fonte: a mensagem só nasce com `false` quando a Meta recusou
 * o envio (o caminho de sucesso grava `true`).
 */
/**
 * Botões do template, na ordem em que a Meta os entrega.
 *
 * Reaproveita o mesmo leitor do editor de fluxo (`botoesDoTemplate`): o que o
 * operador vê no chat e o que ele montou no fluxo têm de ser a mesma lista, e duas
 * leituras do `buttonsJson` divergiriam na primeira variante nova de botão.
 */
const cBotoes = computed(() => botoesDoTemplate(cTemplate.value as never));

const cFalhou = computed(() => props.message.metaTemplateData?.sendSuccess === false)
const cMotivoFalha = computed(() =>
    props.message.metaTemplateData?.erro
    || 'A Meta recusou o envio deste template. Verifique os limites e o método de pagamento da conta.')

const cTemMidiaNoCabecalho = computed(() =>
    cTypes.value.header == 'MEDIA_PNG_JPG_JPEG'
    || cTypes.value.header == 'MEDIA_PDF'
    || cTypes.value.header == 'MEDIA_MP4');

const cHeaderFileData = computed(() => cTypes.value.header == 'MEDIA_MP4' || cTypes.value.header == 'MEDIA_PDF' || cTypes.value.header == 'MEDIA_PNG_JPG_JPEG' ? ({
    mimeType: MetaUploadsService.metaUploadMimeToMime(props.message.metaTemplateData?.template.metaUpload?.mime),
    name    : props.message.metaTemplateData?.template.metaUpload?.originalFileName,
    size    : String(props.message.metaTemplateData?.template.metaUpload?.fileSizeInBytes),
    url     : props.message.metaTemplateData?.template.metaUpload?.s3FileAddress,
} as FileData): null)
</script>
<style scoped>
/*
 * Largura mandada pela MÍDIA, não pelo texto.
 *
 * 320px é a mesma medida da bolha de pré-visualização do template e da faixa em
 * que a imagem com legenda já vive no chat. Sem o teto, `white-space` normal do
 * corpo esticava a bolha até o limite de uma mensagem de texto e a imagem,
 * `width: 100%`, acompanhava — ficava larga, achatada e com o texto correndo ao
 * lado dela.
 */
.template-com-midia {
    max-width: 320px;
}
.template-com-midia :deep(.message-text),
.template-com-midia :deep(p) {
    overflow-wrap: anywhere;
}

/* Botões do template: mesma forma dos botões do WhatsApp, mas inertes. */
.template-botoes {
    margin: 6px -10px -6px;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
}
.template-botao {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
    padding: 8px 12px;
    border: none;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    background: transparent;
    color: #00a5f4;
    font-size: 13px;
    font-weight: 500;
    /* `not-allowed` seria mentira: o botão funciona, só não aqui. */
    cursor: default;
    opacity: 1;
}
.template-botao:last-child { border-bottom: none; }
.template-botao:disabled { color: #00a5f4; }

[data-bs-theme="dark"] .template-botoes { border-top-color: rgba(255, 255, 255, 0.12); }
[data-bs-theme="dark"] .template-botao { border-bottom-color: rgba(255, 255, 255, 0.08); }
</style>